import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, FlatList, StyleSheet, ActivityIndicator, Alert, Pressable } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { useDb } from '../../db/client';
import { listProjects, createProject } from '../../features/projects/projectRepository';
import { getOpenTimeEntryForProjects } from '../../features/session/timeEntries';
import { calculateElapsedSeconds, formatDurationHms } from '../../domain/timer';
import { filterAndSortProjects, type ProjectStatusFilter } from '../../domain/projectList';
import type { Project, TimeEntry } from '../../db/schema';
import { colors, fonts, radii, spacing } from '../theme';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

const STATUS_FILTERS: ProjectStatusFilter[] = ['all', 'not_started', 'in_progress', 'finished', 'cancelled'];

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

export function ProjectsListScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { db, ready } = useDb();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>('all');
  const [query, setQuery] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);

  // Projeto com cronômetro rodando AGORA (spec desta etapa: "sinalizar e
  // manter no topo") — separado de `projects`/ordenação normal porque
  // `last_stitch_at` só é materializado ao PAUSAR (ver pauseTimeEntry em
  // features/session/timeEntries.ts); sem isso, uma sessão em andamento não
  // teria nenhum sinal na lista até a usuária pausar.
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [now, setNow] = useState(new Date());

  const loadProjects = useCallback(async () => {
    if (!ready || !db || !user) return;
    setLoading(true);
    try {
      const result = await listProjects(db, user.id);
      setProjects(result);
      const openEntry = await getOpenTimeEntryForProjects(db, result.map((p) => p.id));
      setActiveEntry(openEntry);
      setNow(new Date());
    } catch (error) {
      console.error('ProjectsListScreen: falha ao carregar projetos', error);
    } finally {
      setLoading(false);
    }
  }, [ready, db, user]);

  // Mesmo padrão de SessionScreen: o `setInterval` só força um re-render
  // mudando `now`, o tempo exibido é sempre recalculado a partir de
  // `activeEntry.startedAt` (nunca acumulado em memória).
  useEffect(() => {
    if (!activeEntry) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [activeEntry]);

  const activeProject = activeEntry ? projects.find((p) => p.id === activeEntry.projectId) ?? null : null;

  const activeElapsedSeconds = useMemo(
    () =>
      activeEntry
        ? calculateElapsedSeconds({ openSessionStartedAt: activeEntry.startedAt, closedSessions: [], now })
        : 0,
    [activeEntry, now]
  );

  // useFocusEffect (não useEffect simples): a tela de detalhe/exclusão
  // (app/projeto/[id].tsx) fica FORA do grupo (tabs) — ao voltar dela com
  // router.back(), esta tela pode continuar montada em segundo plano sem
  // remontar, então um useEffect de montagem não recarregaria a lista após
  // excluir/criar um projeto por lá. Recarregar a cada foco garante que um
  // projeto excluído (soft delete) some da lista assim que a usuária volta
  // a ver esta tela (critério de aceite da S02).
  useFocusEffect(
    useCallback(() => {
      loadProjects();
    }, [loadProjects])
  );

  const handleCreate = async () => {
    if (!db || !user) return;
    const name = newProjectName.trim();
    if (!name) {
      Alert.alert(t('project.list.createErrorTitle'), t('project.list.nameRequiredMessage'));
      return;
    }

    setCreating(true);
    try {
      // Sempre via createProject (insertRecord por baixo: SQLite + outbox
      // na mesma transação) — nunca grava direto no Supabase (regra
      // invíolável #2 do projeto: offline-first).
      const project = await createProject(db, user.id, name);
      setProjects((current) => [...current, project]);
      setNewProjectName('');
    } catch (error) {
      console.error('ProjectsListScreen: falha ao criar projeto', error);
      Alert.alert(t('project.list.createErrorTitle'), (error as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const visibleProjects = filterAndSortProjects(projects, { status: statusFilter, query });

  return (
    <View style={styles.container}>
      <Heading style={styles.title}>{t('tabs.projects.title')}</Heading>

      {activeProject && (
        <Pressable style={styles.activeCard} onPress={() => router.push(`/sessao/${activeProject.id}`)}>
          <View>
            <Text style={styles.activeLabel}>{t('project.list.activeSessionLabel')}</Text>
            <Text style={styles.activeName}>{activeProject.name}</Text>
          </View>
          <Text style={styles.activeTimer}>{formatDurationHms(activeElapsedSeconds)}</Text>
        </Pressable>
      )}

      <View style={styles.createRow}>
        <TextField
          style={styles.createInput}
          placeholder={t('project.list.newNamePlaceholder')}
          value={newProjectName}
          onChangeText={setNewProjectName}
          editable={!creating}
          onSubmitEditing={handleCreate}
        />
        {creating ? (
          <ActivityIndicator color={colors.sageDeep} />
        ) : (
          <Button title={t('project.list.createButton')} onPress={handleCreate} disabled={!db} />
        )}
      </View>

      <TextField
        placeholder={t('project.list.searchPlaceholder')}
        value={query}
        onChangeText={setQuery}
      />

      <View style={styles.filterRow}>
        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter}
            label={t(filter === 'all' ? 'project.list.filterAll' : `project.status.${filter}`)}
            active={statusFilter === filter}
            onPress={() => setStatusFilter(filter)}
          />
        ))}
      </View>

      {!ready || loading ? (
        <Text style={styles.muted}>{t('project.list.loading')}</Text>
      ) : (
        <FlatList
          style={styles.list}
          data={visibleProjects}
          keyExtractor={(item) => item.id}
          ListEmptyComponent={<Text style={styles.muted}>{t('project.list.emptyState')}</Text>}
          renderItem={({ item }) => (
            <Pressable style={styles.row} onPress={() => router.push(`/projeto/${item.id}`)}>
              <Text style={styles.rowName}>{item.name}</Text>
              <Text style={styles.rowStatus}>{t(`project.status.${item.status}`)}</Text>
              <Text style={styles.rowMeta}>
                {item.lastStitchAt
                  ? t('project.list.lastStitchAt', { date: formatDate(item.lastStitchAt) })
                  : t('project.list.lastStitchNever')}
              </Text>
            </Pressable>
          )}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, backgroundColor: colors.sand },
  title: { textAlign: 'left', marginBottom: spacing.md },
  activeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.sageDeep,
    borderRadius: radii.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  activeLabel: { fontFamily: fonts.bodyMedium, fontSize: 12, color: colors.onSage, opacity: 0.85 },
  activeName: { fontFamily: fonts.bodyBold, fontSize: 16, color: colors.onSage, marginTop: 2 },
  activeTimer: { fontFamily: fonts.bodyBold, fontSize: 20, fontVariant: ['tabular-nums'], color: colors.onSage },
  createRow: { flexDirection: 'row', alignItems: 'center' },
  createInput: { flex: 1, marginRight: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap' },
  list: { flex: 1 },
  row: { paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.line },
  rowName: { fontFamily: fonts.bodyBold, fontSize: 16, color: colors.ink },
  rowStatus: { fontFamily: fonts.bodyMedium, fontSize: 13, color: colors.sageDeep },
  rowMeta: { fontFamily: fonts.body, fontSize: 12, color: colors.inkSoft },
  muted: { fontFamily: fonts.body, color: colors.thread, marginTop: spacing.md, textAlign: 'center' },
});
